// Plain date arithmetic for the calendar, in local time. Days are compared
// by their calendar date, never by the clock.

// Sunday is 0, as in `Date.getDay()`.
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

// Locales the app supports that start the week on Sunday. The rest start on
// Monday. Newer browsers know this themselves, see `firstDayOfWeek`.
const SUNDAY_FIRST = new Set(['en-US', 'en-CA']);

// The weekday a calendar's rows start on, for a locale.
export const firstDayOfWeek = (locale: string): Weekday => {
  try {
    // Chrome 130+ and Safari 17+. Their firstDay is 1 (Monday) to 7 (Sunday).
    const info = new Intl.Locale(locale) as unknown as {
      getWeekInfo?: () => { firstDay: number };
      weekInfo?: { firstDay: number };
    };
    const firstDay = (info.getWeekInfo?.() ?? info.weekInfo)?.firstDay;
    if (firstDay) {
      return (firstDay % 7) as Weekday;
    }
  } catch {
    // An unknown locale string: fall through to the list.
  }
  return SUNDAY_FIRST.has(locale) ? 0 : 1;
};

// The seven weekdays in a calendar's column order.
export const weekdayOrder = (first: Weekday): Weekday[] =>
  Array.from({ length: 7 }, (_, i) => ((first + i) % 7) as Weekday);

export const startOfDay = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), date.getDate());

export const startOfMonth = (date: Date): Date =>
  new Date(date.getFullYear(), date.getMonth(), 1);

export const isSameDay = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() &&
  a.getMonth() === b.getMonth() &&
  a.getDate() === b.getDate();

export const isSameMonth = (a: Date, b: Date): boolean =>
  a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth();

export const addDays = (date: Date, days: number): Date => {
  const next = new Date(date);
  next.setDate(next.getDate() + days);
  return next;
};

// The same day of the month `months` later, or the last day of that month
// when it's shorter (31 January + 1 → 28 February).
export const addMonths = (date: Date, months: number): Date => {
  const next = new Date(date);
  next.setDate(1);
  next.setMonth(next.getMonth() + months);
  const lastDay = new Date(next.getFullYear(), next.getMonth() + 1, 0);
  next.setDate(Math.min(date.getDate(), lastDay.getDate()));
  return next;
};

// The six weeks shown for a month: 42 days from the first `weekStartsOn`
// on or before the 1st, so every month fills the same grid.
export const monthGrid = (month: Date, weekStartsOn: Weekday): Date[] => {
  const first = startOfMonth(month);
  const offset = (first.getDay() - weekStartsOn + 7) % 7;
  const start = addDays(first, -offset);
  return Array.from({ length: 42 }, (_, i) => addDays(start, i));
};

// The same time of day on another day.
export const withDay = (base: Date, day: Date): Date => {
  const next = new Date(base);
  next.setFullYear(day.getFullYear(), day.getMonth(), day.getDate());
  return next;
};
